import { omit } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useState } from 'react';

import ToggleButton from 'dashboard/apps/InquiryTemplateEditor/ToggleButton';
import NavBack from 'dashboard/components/NavBack';
import Pii from 'dashboard/components/Pii';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop, CardContainer, Container } from 'dashboard/layouts/ShowViewLayoutV2';
import { ApiLog } from 'dashboard/models/api-log';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';

interface DashboardApiLogsShowViewProps {
  apiLog: ApiLog;
}

const TAB_WIDTH = 2;

const DashboardApiLogsShowView = ({ apiLog }: DashboardApiLogsShowViewProps) => {
  const [showUnfilteredRequest, setShowUnfilteredRequest] = useState(false);
  const [showUnfilteredResponse, setShowUnfilteredResponse] = useState(false);

  if (apiLog == null) {
    return (
      <Backdrop>
        <NullState message='We couldn’t find that API log.' backRoute='/dashboard/api-logs' />
      </Backdrop>
    );
  }

  const title = `${apiLog.attributes.request.method} ${apiLog.attributes.request.path}`;

  const sanitizedApiLogExplainer = (
    <Paragraph>Note: Sanitized API logs have possible PII removed.</Paragraph>
  );
  const unsanitizedApiLogExplainer = (
    <Paragraph>
      Note: Unsanitized API logs are only viewable by Admins of this organization.
    </Paragraph>
  );

  return (
    <Backdrop>
      <Container>
        <NavBack to='/dashboard/api-logs' dataTest='api-log-show__nav-back'>
          {title}
        </NavBack>
        <Spacer height={2} />
        <CardContainer>
          <Card>
            <Card.Body>
              <Card.Subtitle>Response code</Card.Subtitle>
              <Card.Title>{apiLog.attributes.response.status}</Card.Title>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Subtitle>Request timestamp</Card.Subtitle>
              <Card.Title>
                {DateTime.fromISO(apiLog.attributes['created-at']).toFormat(
                  DATE_AND_TIME_TZ_FORMAT,
                )}
              </Card.Title>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Subtitle>IP Address</Card.Subtitle>
              <Card.Title>{apiLog.attributes.request['ip-address']}</Card.Title>
            </Card.Body>
          </Card>
        </CardContainer>

        <Card>
          <Card.Body>
            <Card.Title>API request headers</Card.Title>
            <Pii isRedacted={false}>
              <CodeHighlight id='api-log-show__headers' language='json' rowsToShow={20}>
                {JSON.stringify(apiLog.attributes.request.headers, null, TAB_WIDTH)}
              </CodeHighlight>
            </Pii>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Card.Title>API request</Card.Title>
            {apiLog.attributes['request-unfiltered'] && (
              <ToggleButton
                inactiveButtonLabel='Sanitized'
                activeButtonLabel='Unsanitized'
                setIsActive={setShowUnfilteredRequest}
                isActive={showUnfilteredRequest}
              />
            )}
            {showUnfilteredRequest ? (
              <>
                {unsanitizedApiLogExplainer}
                <Pii isRedacted={false}>
                  <CodeHighlight
                    id='api-log-show__request-unfiltered'
                    language='json'
                    rowsToShow={20}
                  >
                    {JSON.stringify(
                      omit('headers', apiLog.attributes['request-unfiltered']),
                      null,
                      TAB_WIDTH,
                    )}
                  </CodeHighlight>
                </Pii>
              </>
            ) : (
              <>
                {sanitizedApiLogExplainer}
                <Pii isRedacted={false}>
                  <CodeHighlight id='api-log-show__request' language='json' rowsToShow={20}>
                    {JSON.stringify(omit('headers', apiLog.attributes.request), null, TAB_WIDTH)}
                  </CodeHighlight>
                </Pii>
              </>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Card.Title>API response</Card.Title>
            {apiLog.attributes['response-unfiltered'] && (
              <ToggleButton
                inactiveButtonLabel='Sanitized'
                activeButtonLabel='Unsanitized'
                setIsActive={setShowUnfilteredResponse}
                isActive={showUnfilteredResponse}
              />
            )}
            {showUnfilteredResponse ? (
              <>
                {unsanitizedApiLogExplainer}
                <Pii isRedacted={false}>
                  <CodeHighlight
                    id='api-log-show__response-unfiltered'
                    language='json'
                    rowsToShow={20}
                  >
                    {JSON.stringify(apiLog.attributes['response-unfiltered'], null, TAB_WIDTH)}
                  </CodeHighlight>
                </Pii>
              </>
            ) : (
              <>
                {sanitizedApiLogExplainer}
                <Pii isRedacted={false}>
                  <CodeHighlight id='api-log-show__response' language='json' rowsToShow={20}>
                    {JSON.stringify(apiLog.attributes.response, null, TAB_WIDTH)}
                  </CodeHighlight>
                </Pii>
              </>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Card.Title>Related Objects</Card.Title>
            <CodeHighlight id='api-log-show__related_object_ids' language='json' rowsToShow={20}>
              {JSON.stringify(apiLog.attributes['related-object-ids'], null, TAB_WIDTH)}
            </CodeHighlight>
          </Card.Body>
        </Card>
      </Container>
    </Backdrop>
  );
};

export default DashboardApiLogsShowView;
